import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, RefreshCw, Plus, Loader2, Search } from "lucide-react";
import { getAwaitingInvoices, fetchXeroInvoices, getStats } from "../../services/dataService";
import { DataTable } from "../DataTable";
import { PageHeader, StatCard } from "../PageHeader";
import { StatusBadge } from "../StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { AddToBatchModal } from "../modals/AddToBatchModal";
import { formatCurrency, formatDate } from "../../lib/format";
import { toast } from "sonner";

export function InvoicesManager({ onGoToBatches }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const { data: all = [], isLoading } = useQuery({ queryKey: ["invoices"], queryFn: getAwaitingInvoices });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: getStats });

  const awaiting = all.filter((i) => i.status === "Awaiting Payment");
  const filtered = awaiting.filter(
    (i) =>
      (i.client_name || "").toLowerCase().includes(q.toLowerCase()) ||
      (i.invoice_number || "").toLowerCase().includes(q.toLowerCase()) ||
      (i.reference || "").toLowerCase().includes(q.toLowerCase())
  );

  const refresh = useMutation({
    mutationFn: fetchXeroInvoices,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(`Xero sync complete — ${data.new_invoices} new invoice(s). ${data.total_awaiting} awaiting.`);
    },
    onError: () => toast.error("Xero refresh failed"),
  });

  const toggleRow = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = (val) => setSelected(val ? filtered.map((i) => i.id) : []);

  const columns = [
    { key: "invoice_number", header: "Invoice #", sortable: true, mono: true },
    { key: "client_name", header: "Client Name", sortable: true },
    { key: "reference", header: "Reference", mono: true, render: (r) => <span className="text-slate-500">{r.reference}</span> },
    { key: "due_date", header: "Due Date", sortable: true, accessor: (r) => new Date(r.due_date).getTime(), render: (r) => formatDate(r.due_date) },
    { key: "amount", header: "Amount", sortable: true, align: "right", mono: true, render: (r) => formatCurrency(r.amount) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  const selectedTotal = awaiting.filter((i) => selected.includes(i.id)).reduce((s, i) => s + i.amount, 0);

  return (
    <div>
      <PageHeader title="Invoices" icon={FileText} description="Awaiting-payment invoices pulled from Xero. Select invoices and add them to a batch for Netcash processing.">
        <Button onClick={() => refresh.mutate()} disabled={refresh.isPending} className="bg-sky-600 hover:bg-sky-700 text-white font-semibold" data-testid="refresh-from-xero-button">
          <RefreshCw className={`h-4 w-4 mr-2 ${refresh.isPending ? "animate-spin" : ""}`} />
          Refresh from Xero
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard label="Awaiting Invoices" value={stats?.awaiting_invoices ?? awaiting.length} accent="blue" />
        <StatCard label="Awaiting Value" value={formatCurrency(stats?.awaiting_value ?? awaiting.reduce((s, i) => s + i.amount, 0))} accent="slate" />
        <StatCard label="Selected" value={selected.length} hint={selected.length ? formatCurrency(selectedTotal) : "None selected"} accent="emerald" />
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
        <div className="relative max-w-sm w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, invoice # or reference…" className="pl-9" data-testid="invoices-search-input" />
        </div>
        <Button onClick={() => setModalOpen(true)} disabled={selected.length === 0} className="bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 text-white font-semibold" data-testid="add-to-batch-button">
          <Plus className="h-4 w-4 mr-2" /> Add to Batch {selected.length > 0 && `(${selected.length})`}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          initialSort={{ key: "due_date", dir: "asc" }}
          selectable
          selectedIds={selected}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          emptyText="No awaiting-payment invoices. Click 'Refresh from Xero' to pull the latest."
          testId="invoices-table"
        />
      )}

      <AddToBatchModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        invoiceIds={selected}
        onSuccess={() => { setSelected([]); onGoToBatches?.(); }}
      />
    </div>
  );
}
